// Import the derived type from constants
import type {KnownToolName} from '../constants/tool-names'
import type {COMMAND_TYPES} from '../constants/command-types'
import type {CopilotAnnotation} from './code-insights-metadata'
import type {ToolLogMetadataBag} from './tool-log-metadata'

export type MessageRole = 'user' | 'assistant' | 'tool'

export type Delta = {
  content?: string
  assistant_content?: string
  role?: MessageRole
  reasoning_text?: string
  tool_calls?: Array<{
    function?: {
      arguments?: string
      name: ToolName
      result?: string
    }
    custom?: {
      input?: string
      name: ToolName
    }
    id: string
    type: string
    index: number
  }>
  copilot_annotations?: CopilotAnnotation | string
}

export type StreamingMessage = {
  type?: string
  id: string
  created: number
  model: string
  object: string
  choices: Choice[]
}

export type Choice = {
  finish_reason: string
  delta: Delta
}

export type CompletionChunk = {
  id: string
  choices: Choice[]
}

export type CommandMessage = CompletionUserMessage & {source: string}

export type CompletionUserMessage = {
  role: 'user'
  content: string | Array<{type: 'text'; text: string} | {type: 'image_url' | 'input_audio' | 'file'}>
  source: string
  created?: number
}

export type CompletionToolMessage = {
  role: 'tool'
  tool_call_id: string
  content: string | Array<{type: 'text'; text: string}>
}

export type ChatMessage = {
  id: string
  threadID: string
  role: 'user' | 'assistant'
  content?: string
  choices?: Choice[]
}

/**
 * A single property in a requestedSchema, representing one field the agent wants the user to fill.
 */
export type RequestedSchemaProperty = {
  type?: 'string' | 'number' | 'boolean' | 'integer' | 'array' | 'object'
  title?: string
  description?: string
  enum?: string[]
  /** Display labels for enum values, keyed by enum value. Derived from oneOf/anyOf titles. */
  enumLabels?: Record<string, string>
  default?: string
  /** True when the field expects multiple selections (derived from `type: "array"`). */
  multiSelect?: boolean
}

/**
 * Structured schema the CLI agent can send for multi-field ask_user prompts.
 * Follows a subset of JSON Schema with `properties` mapping field names to
 * their definitions, and an optional `required` array listing mandatory fields.
 */
export type RequestedSchema = {
  properties: Record<string, RequestedSchemaProperty>
  required?: string[]
}

/**
 * Legacy ask_user: flat choice list and/or freeform text.
 * Response contract: { answer: string, wasFreeform: boolean }
 */
export type SimpleAskUserArguments = {
  question: string
  choices?: string[]
  allowFreeform?: boolean
}

/**
 * Elicitation-style ask_user: structured multi-field form.
 * Matches the wire format from the CLI's elicitation tool.
 * Response contract: { action: "accept" | "decline" | "cancel", content?: Record<string, unknown> }
 */
export type ElicitationAskUserArguments = {
  message: string
  requestedSchema: RequestedSchema
}

/**
 * Discriminated union of the two ask_user argument shapes.
 * Use `isElicitationAskUser()` / `isSimpleAskUser()` to narrow.
 */
export type AskUserArguments = SimpleAskUserArguments | ElicitationAskUserArguments

/** Type guard: true when the ask_user prompt uses the elicitation/schema format */
export function isElicitationAskUser(args: AskUserArguments): args is ElicitationAskUserArguments {
  return (
    'requestedSchema' in args &&
    !!args.requestedSchema &&
    typeof args.requestedSchema === 'object' &&
    !!args.requestedSchema.properties &&
    Object.keys(args.requestedSchema.properties).length > 0
  )
}

/** Type guard: true when the ask_user prompt uses the simple choices format */
export function isSimpleAskUser(args: AskUserArguments): args is SimpleAskUserArguments {
  return !isElicitationAskUser(args)
}

export type ExitPlanModeArguments = {
  summary: string
  actions?: string[]
  recommendedAction?: string
}

export type PermissionRequestArguments = {
  kind: string
  toolName?: string
  command?: string
  filePath?: string
  diff?: string
  description?: string
}

/** Interactive prompt tool names - these tools require user interaction */
export type InteractivePromptToolName = 'ask_user' | 'exit_plan_mode' | 'permission_request'

/** Set of interactive prompt tool names for runtime checks */
export const INTERACTIVE_PROMPT_TOOL_NAMES = new Set<InteractivePromptToolName>([
  'ask_user',
  'exit_plan_mode',
  'permission_request',
])

export type ToolUserRequestedMessage = {
  type: 'tool.user_requested'
  toolCallId: string
  toolName: InteractivePromptToolName
  arguments: AskUserArguments | ExitPlanModeArguments | PermissionRequestArguments
  timestamp: string
}

export type ToolExecutionStartMessage = {
  type: 'tool.execution_start'
  id: string
  timestamp: string
  data: {
    toolCallId: string
    toolName: string
    arguments: unknown
    parentToolCallId?: string
  }
}

// tool.outputs event indicates a tool call (including permission prompts) has completed
export type ToolOutputsMessage = {
  type: 'tool.outputs'
  toolCallId: string
  output: string
  timestamp: string
}

export type LogEntry =
  | CompletionChunk
  | CompletionUserMessage
  | CompletionToolMessage
  | ToolUserRequestedMessage
  | ToolExecutionStartMessage
  | ToolOutputsMessage
  | Record<string, unknown>

/**
 * Streaming chat messages from intent detection
 */

export type StreamingChatMessage = StreamingChatTaskMetadata | StreamingChatChunk

export type StreamingChatTaskMetadata = {
  type: 'metadata'
  task_id: string
  session_id: string
}

export type StreamingChatChunk = {
  type: 'content'
  body: string
}

/**
 * Preprocessed log entry - deduplicated and normalized for rendering.
 */
export type PreprocessedLogEntry =
  | CompletionChunk
  | CompletionToolMessage
  | ChatMessage
  | Record<string, unknown>
  | CompletionUserMessage[]

/**
 * Maps tool_call_id to its associated metadata bag.
 */
export type ToolCallMetadataMap = Record<string, ToolLogMetadataBag>

/**
 * Hydrated log entry - preprocessed entry with tool-call-scoped metadata attached.
 */
export type HydratedLogEntry = PreprocessedLogEntry & {
  tool_call_metadata?: ToolCallMetadataMap
}

export function isCompletionChunk(logEntry: HydratedLogEntry): logEntry is CompletionChunk {
  if (typeof logEntry !== 'object' || logEntry === null) {
    return false
  }

  return 'choices' in logEntry && Array.isArray(logEntry.choices)
}

export function isCommandGroup(logEntry: HydratedLogEntry): logEntry is CompletionUserMessage[] {
  return Array.isArray(logEntry) && logEntry.every(entry => isCommandMessage(entry))
}

export function isCommandMessage(logEntry: LogEntry): logEntry is CommandMessage {
  return isCompletionUserMessage(logEntry) && 'source' in logEntry
}

export function isCompletionUserMessage(logEntry: LogEntry): logEntry is CompletionUserMessage {
  if (typeof logEntry !== 'object' || logEntry === null) {
    return false
  }
  return 'role' in logEntry && logEntry.role === 'user'
}

export function isCompletionToolMessage(logEntry: LogEntry): logEntry is CompletionToolMessage {
  if (typeof logEntry !== 'object' || logEntry === null) {
    return false
  }
  return 'role' in logEntry && logEntry.role === 'tool'
}

export function isChatMessage(logEntry?: LogEntry): logEntry is ChatMessage {
  if (typeof logEntry !== 'object' || logEntry === null || logEntry === undefined) {
    return false
  }
  return 'role' in logEntry && (logEntry.role === 'user' || logEntry.role === 'assistant') && 'threadID' in logEntry
}

export function isToolUserRequestedMessage(logEntry: LogEntry): logEntry is ToolUserRequestedMessage {
  if (typeof logEntry !== 'object' || logEntry === null) {
    return false
  }

  if (!('type' in logEntry) || logEntry.type !== 'tool.user_requested') {
    return false
  }

  if (!('toolCallId' in logEntry) || typeof logEntry.toolCallId !== 'string') {
    return false
  }

  if (!('toolName' in logEntry) || typeof logEntry.toolName !== 'string') {
    return false
  }

  if (!('arguments' in logEntry) || typeof logEntry.arguments !== 'object') {
    return false
  }

  if (!('timestamp' in logEntry) || typeof logEntry.timestamp !== 'string') {
    return false
  }

  return true
}

export function isToolExecutionStartMessage(logEntry: LogEntry): logEntry is ToolExecutionStartMessage {
  if (typeof logEntry !== 'object' || logEntry === null) {
    return false
  }

  if (!('type' in logEntry) || logEntry.type !== 'tool.execution_start') {
    return false
  }

  if (!('id' in logEntry) || typeof logEntry.id !== 'string') {
    return false
  }

  if (!('timestamp' in logEntry) || typeof logEntry.timestamp !== 'string') {
    return false
  }

  if (!('data' in logEntry) || typeof logEntry.data !== 'object' || logEntry.data === null) {
    return false
  }

  const data = logEntry.data as Record<string, unknown>
  if (typeof data.toolCallId !== 'string' || typeof data.toolName !== 'string') {
    return false
  }

  return true
}

export function isToolOutputsMessage(logEntry: LogEntry): logEntry is ToolOutputsMessage {
  if (typeof logEntry !== 'object' || logEntry === null) {
    return false
  }

  if (!('type' in logEntry) || logEntry.type !== 'tool.outputs') {
    return false
  }

  if (!('toolCallId' in logEntry) || typeof logEntry.toolCallId !== 'string') {
    return false
  }

  if (!('output' in logEntry) || typeof logEntry.output !== 'string') {
    return false
  }

  if (!('timestamp' in logEntry) || typeof logEntry.timestamp !== 'string') {
    return false
  }

  return true
}

export function isAskUserPrompt(message: ToolUserRequestedMessage): boolean {
  return message.toolName === 'ask_user'
}

export function isExitPlanModePrompt(message: ToolUserRequestedMessage): boolean {
  return message.toolName === 'exit_plan_mode'
}

export function isPermissionRequestPrompt(message: ToolUserRequestedMessage): boolean {
  return message.toolName === 'permission_request'
}

/**
 * Type guard to validate AskUserArguments structure at runtime.
 * Accepts both canonical (`question`) and CLI (`message` + `requestedSchema`) formats.
 */
export function isAskUserArguments(args: unknown): args is AskUserArguments {
  if (typeof args !== 'object' || args === null) return false
  const obj = args as Record<string, unknown>

  // Elicitation format: message + structurally valid requestedSchema
  if (typeof obj.message === 'string' && obj.requestedSchema && typeof obj.requestedSchema === 'object') {
    const schema = obj.requestedSchema as Record<string, unknown>
    if (schema.properties && typeof schema.properties === 'object' && Object.keys(schema.properties).length > 0) {
      return true
    }
  }

  // Simple format: must have question
  if (typeof obj.question === 'string') {
    if (obj.choices !== undefined && !Array.isArray(obj.choices)) return false
    if (obj.allowFreeform !== undefined && typeof obj.allowFreeform !== 'boolean') return false
    return true
  }

  return false
}

/**
 * Type guard to validate ExitPlanModeArguments structure at runtime.
 */
export function isExitPlanModeArguments(args: unknown): args is ExitPlanModeArguments {
  if (typeof args !== 'object' || args === null) return false
  const obj = args as Record<string, unknown>
  if (typeof obj.summary !== 'string') return false
  if (obj.actions !== undefined && !Array.isArray(obj.actions)) return false
  if (obj.recommendedAction !== undefined && typeof obj.recommendedAction !== 'string') return false
  return true
}

/**
 * Type guard to validate PermissionRequestArguments structure at runtime.
 */
export function isPermissionRequestArguments(args: unknown): args is PermissionRequestArguments {
  if (typeof args !== 'object' || args === null) return false
  const obj = args as Record<string, unknown>
  if (typeof obj.kind !== 'string') return false
  if (obj.toolName !== undefined && typeof obj.toolName !== 'string') return false
  if (obj.command !== undefined && typeof obj.command !== 'string') return false
  if (obj.filePath !== undefined && typeof obj.filePath !== 'string') return false
  if (obj.diff !== undefined && typeof obj.diff !== 'string') return false
  if (obj.description !== undefined && typeof obj.description !== 'string') return false
  return true
}

export function isValidLogEntry(message: unknown): message is LogEntry {
  if (typeof message !== 'object' || message === null) {
    return false
  }

  return (
    isCompletionChunk(message as LogEntry) ||
    isCompletionUserMessage(message as LogEntry) ||
    isCompletionToolMessage(message as LogEntry) ||
    isToolUserRequestedMessage(message as LogEntry) ||
    isToolExecutionStartMessage(message as LogEntry) ||
    typeof message === 'object'
  )
}

export type AgentInfo = {
  id?: string
  slug: string
  display_name: string
  avatar_url: string
  doc_url: string
  /** Global Relay (GraphQL node) ID for the agent's Bot user */
  bot_global_relay_id?: string
}

export type SelectedAgent = {
  typeName?: 'topLevel' | 'customSubAgent'
  topLevelAgentId?: string
  subAgentSlug?: string
  /** Display name of the agent (e.g. "Copilot", "Codex", "My Custom Agent") */
  displayName?: string
  /** Avatar URL for the agent */
  avatarUrl?: string
  /** Login/slug identifier for the agent (e.g. "copilot-swe-agent", "codex") */
  login?: string
  /** Global Relay (GraphQL node) ID for the agent's Bot user */
  botGlobalRelayId?: string
}

export type Session = {
  id: string
  name: string
  user_id: number
  agent_id: number
  state: SessionState
  owner_id: number
  repo_id: number
  task_id?: string
  resource_type: string
  resource_id: number
  resource_global_id: string
  created_at: string // ISO 8601 string
  /** @deprecated Use updated_at when copilot_task_api_github_rest_style is enabled */
  last_updated_at?: string // ISO 8601 string
  /** New field name used when copilot_task_api_github_rest_style is enabled */
  updated_at?: string // ISO 8601 string
  completed_at?: string // Optional ISO 8601 string,
  workflow_run_id: number
  log_entries: LogEntry[]
  error: {
    message: string
  } | null
  premium_requests: number
  event_identifiers: string[] | null
  event_type: string | null
  event_url: string | null
  event_content: string | null
  isPending?: boolean
  model?: string
}

export function isReadonlyAgent(session: Session | null) {
  if (!session) return false
  // these are all CCR for now, in different deployment environments
  // ideally Soon we can check some integration ID string like `copilot-4-prs`
  // ex: https://github.com/github/copilot-api/blob/6eabc495bab5ad7450ccbf5333218c9cc1acff43/config/kubernetes/production/deployments/copilot-api.yaml#L115
  return [946600, 71, 106, 776, 386, 56].includes(session.agent_id)
}

// TODO: Improve this type as we learn more about the real data
export const SessionState = {
  InProgress: 'in_progress',
  Completed: 'completed',
  Failed: 'failed',
  Idle: 'idle',
  WaitingForUser: 'waiting_for_user',
  TimedOut: 'timed_out',
  Cancelled: 'cancelled',
  Queued: 'queued',
} as const

export type SessionState = (typeof SessionState)[keyof typeof SessionState]

const SESSION_STATES_SET = new Set<SessionState>(Object.values(SessionState))

export function isSessionState(state: string): state is SessionState {
  return SESSION_STATES_SET.has(state as SessionState)
}

export function getReadableSessionState(state: SessionState, options?: {hasPull?: boolean}) {
  switch (state) {
    case SessionState.InProgress:
      return 'In progress'
    case SessionState.Completed:
      return options?.hasPull ? 'Ready for review' : 'Completed'
    case SessionState.Failed:
      return 'Failed'
    case SessionState.Idle:
      return 'Idle'
    case SessionState.WaitingForUser:
      return 'Waiting for user'
    case SessionState.TimedOut:
      return 'Timed out'
    case SessionState.Cancelled:
      return 'Cancelled'
    case SessionState.Queued:
      return 'Queued'
    default:
      return 'Unknown'
  }
}

export function isActiveSession(session: Session) {
  return ['queued', 'in_progress'].includes(session.state)
}

export function isQueuedSession(session: Session) {
  return session.state === SessionState.Queued
}

export function isCompletedSession(session: Session) {
  return session.state === SessionState.Completed
}

export function isPendingSession(session: Session) {
  return !!session.isPending && session.state === SessionState.Queued
}

export function isCancelledSession(session: Session) {
  return session.state === SessionState.Cancelled
}

export function isFailedSession(session: Session) {
  return session.state === SessionState.Failed
}

/**
 * Tool names that can be used by the agent.
 * Includes all known tools (from tool-names.ts) plus any custom MCP tool.
 */
export type ToolName = KnownToolName | string

export type BashToolArgs = {
  command: string
  description?: string
}

export type ReadBashToolArgs = {
  shellId: string
  /** @deprecated Use shellId. Kept for backwards compat with old sessions. */
  sessionId?: string
}

export type WriteBashToolArgs = {
  shellId: string
  /** @deprecated Use shellId. Kept for backwards compat with old sessions. */
  sessionId?: string
  input: string
}

export type StopBashToolArgs = {
  shellId: string
  /** @deprecated Use shellId. Kept for backwards compat with old sessions. */
  sessionId?: string
}

export type AsyncBashToolArgs = {
  command: string
  shellId?: string
  /** @deprecated Use shellId. Kept for backwards compat with old sessions. */
  sessionId?: string
}

export type ReadAsyncBashToolArgs = {
  shellId?: string
  /** @deprecated Use shellId. Kept for backwards compat with old sessions. */
  sessionId?: string
}

export type StopAsyncBashToolArgs = {
  shellId?: string
  /** @deprecated Use shellId. Kept for backwards compat with old sessions. */
  sessionId?: string
}

export type ListBashToolArgs = Record<string, unknown>

export type PowerShellToolArgs = {
  command: string
  description?: string
}

export type ReadPowerShellToolArgs = {
  shellId: string
  /** @deprecated Use shellId. Kept for backwards compat with old sessions. */
  sessionId?: string
}

export type WritePowerShellToolArgs = {
  shellId: string
  /** @deprecated Use shellId. Kept for backwards compat with old sessions. */
  sessionId?: string
  input: string
}

export type StopPowerShellToolArgs = {
  shellId: string
  /** @deprecated Use shellId. Kept for backwards compat with old sessions. */
  sessionId?: string
}

export type ListPowerShellToolArgs = Record<string, unknown>

export type ThinkToolArgs = {
  thought: string
}

export type RunCustomSetupStepToolArgs = {
  name: string
}

export type CodeReviewToolArgs = {
  prTitle: string
  prDescription: string
}

export type SetupMcpDetails = {
  type: 'mcp_setup'
  /**
   * May not be present if setup failed.
   */
  toolNamesToDisplayNames?: Record<string, string>
}

export type SetupDetails = SetupMcpDetails

export type RunSetupArgs = {
  name: string
  details?: SetupDetails
}

export type StrReplaceEditorToolArgs = {
  /**
   * Will be `undefined` when the agent is using discrete editing tools.
   */
  command?: 'view' | 'create' | 'str_replace' | 'insert' | 'undo_edit'
  path: string
  view_range?: [number, number]
  insert_line?: number
}

export type ReportProgressToolArgs =
  | {
      prDescription: string
      commitMessage: string | undefined
    }
  | {
      /**
       * A markdown checklist of the plan as tasks and progress on the tasks completed so far.
       */
      tasksChecklist: string
      /**
       * The next incomplete task to be worked on from the checklist.
       */
      nextTask: string
    }

export type CommitChangesToolArgs = {
  commitMessage: string | undefined
}

export type ReplyToCommentToolArgs = {
  comment_id: string
  reply: string
}

export type GitHubMCPServerWebSearchToolArgs = {
  query: string
}

export type WebFetchToolArgs = {
  url: string
}

export type ProposeWorkToolArgs = {
  workTitle: string
  workDescription: string
  workType: 'code_change' | 'task'
}

export type GrepToolArgs = {
  pattern: string
  path?: string
  output_mode?: string
  glob?: string
  type?: string
  '-i'?: boolean
  '-A'?: number
  '-B'?: number
  '-C'?: number
  '-n'?: boolean
  head_limit?: number
  multiline?: boolean
}

export type GlobToolArgs = {
  pattern: string
  path?: string
}

export type SearchCodeSubagentToolArgs = {
  query: string
}

export type SkillToolArgs = {
  skill: string
}

export type TaskToolArgs = {
  description: string
  prompt: string
  agent_type: string
  model?: string
  mode?: 'sync' | 'background'
}

export type CreatePullRequestToolArgs = {
  title: string
  description: string
  draft: boolean
}

export type ToolArgs =
  | BashToolArgs
  | ThinkToolArgs
  | StrReplaceEditorToolArgs
  | ReportProgressToolArgs
  | ReplyToCommentToolArgs
  | AsyncBashToolArgs
  | ReadAsyncBashToolArgs
  | StopAsyncBashToolArgs
  | ReadBashToolArgs
  | WriteBashToolArgs
  | StopBashToolArgs
  | ListBashToolArgs
  | PowerShellToolArgs
  | ReadPowerShellToolArgs
  | WritePowerShellToolArgs
  | StopPowerShellToolArgs
  | ListPowerShellToolArgs
  | RunCustomSetupStepToolArgs
  | GitHubMCPServerWebSearchToolArgs
  | WebFetchToolArgs
  | CodeReviewToolArgs
  | ProposeWorkToolArgs
  | GrepToolArgs
  | GlobToolArgs
  | SearchCodeSubagentToolArgs
  | SkillToolArgs
  | TaskToolArgs
  | CreatePullRequestToolArgs
  | Record<string, unknown>

/**
 * Response command type values from COMMAND_TYPES.
 */
type ResponseCommandType = (typeof COMMAND_TYPES)[keyof typeof COMMAND_TYPES]

/**
 * Command types for Mission Control ↔ CLI protocol.
 * These determine how the CLI processes incoming commands.
 */
export type CommandType = 'user_message' | ResponseCommandType | 'abort'

export type Command = {
  id: string
  external_id: string | null
  user_id: number
  session_id: string
  content: string
  created_at: string
  updated_at: string
  state: 'queued' | 'in_progress' | 'completed' | 'failed' | 'dismissed'
  type?: CommandType
  error: string | null
}

export type UserCommandState = Command['state']

export type CommandsResponse = {
  commands: Command[]
}

export type SessionIdToLogsMap = Record<Session['id'], LogEntry[]>
